import React, { useState } from "react";
import {
  ChevronDown, ChevronUp, Presentation, Users, Target, BarChart3,
  Shield, Handshake, MessageSquare, HelpCircle, Swords, ClipboardCheck,
  GraduationCap, Mic, BookOpen, TrendingUp, Clock, Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";

const PARTS = [
  { id: "demo", label: "30-Min Demo Script", icon: Presentation },
  { id: "audiences", label: "Audience Guides", icon: Users },
  { id: "sales", label: "Sales Training", icon: Handshake },
];

const Section = ({ num, title, time, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 mb-3" data-testid={`playbook-section-${num}`}>
      <button
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="w-7 h-7 bg-[#002FA7] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{num}</span>
        <span className="font-bold text-sm flex-1">{title}</span>
        {time && <span className="text-[10px] font-bold text-[#002FA7] bg-blue-50 px-2 py-0.5 rounded-full">{time}</span>}
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
};

const ShowBlock = ({ items }) => (
  <div className="bg-slate-50 border-l-3 border-l-[#002FA7] pl-4 pr-3 py-3 rounded-r">
    <div className="text-[10px] font-extrabold text-[#002FA7] tracking-wider mb-2">WHAT TO SHOW</div>
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="text-xs text-slate-600 pl-4 relative before:content-['>'] before:absolute before:left-0 before:text-[#002FA7] before:font-bold before:text-[10px]">{item}</li>
      ))}
    </ul>
  </div>
);

const TalkBlock = ({ text }) => (
  <div className="bg-amber-50 border-l-3 border-l-amber-400 pl-4 pr-3 py-3 rounded-r">
    <div className="text-[10px] font-extrabold text-amber-700 tracking-wider mb-2">TALK TRACK</div>
    <p className="text-xs text-slate-600 italic leading-relaxed">{text}</p>
  </div>
);

const HookBlock = ({ hooks }) => (
  <div className="bg-blue-50 border-l-3 border-l-blue-400 pl-4 pr-3 py-3 rounded-r">
    <div className="text-[10px] font-extrabold text-blue-700 tracking-wider mb-2">AUDIENCE-SPECIFIC HOOKS</div>
    {hooks.map((h, i) => (
      <p key={i} className="text-[11px] text-slate-600 mb-1">
        <span className={`inline-block text-[9px] font-bold px-2 py-0.5 rounded mr-1 ${h.tagClass}`}>{h.tag}</span>
        {h.text}
      </p>
    ))}
  </div>
);

const ObjectionBlock = ({ question, answer }) => (
  <div className="bg-red-50 border-l-3 border-l-red-400 pl-4 pr-3 py-3 rounded-r">
    <div className="text-xs font-bold text-red-800 mb-1">{question}</div>
    <p className="text-xs text-slate-600 italic">{answer}</p>
  </div>
);

const TipBlock = ({ title, items }) => (
  <div className="bg-green-50 border-l-3 border-l-green-400 pl-4 pr-3 py-3 rounded-r">
    <div className="text-[10px] font-extrabold text-green-700 tracking-wider mb-2">{title}</div>
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="text-xs text-slate-600 pl-4 relative before:content-['*'] before:absolute before:left-1 before:text-green-500 before:font-bold">{item}</li>
      ))}
    </ul>
  </div>
);

const TAG = {
  ell: "bg-green-100 text-green-800",
  principal: "bg-blue-100 text-blue-800",
  ap: "bg-violet-100 text-violet-800",
  coach: "bg-amber-100 text-amber-800",
  sales: "bg-pink-100 text-pink-800",
  parent: "bg-yellow-100 text-yellow-800",
};

// ============ PART I: DEMO SCRIPT ============
const DemoScript = () => (
  <div className="space-y-2">
    <div className="bg-[#002FA7] text-white p-4 mb-4">
      <div className="text-[10px] font-bold tracking-widest opacity-70">PART I</div>
      <div className="text-lg font-extrabold">The 30-Minute Full Platform Demo</div>
      <p className="text-xs opacity-80">Complete walkthrough of every major capability. Adapt timing per audience.</p>
    </div>

    <Section num="1" title="The Hook — Start with the Student Experience" time="3 min" defaultOpen={true}>
      <ShowBlock items={[
        "Landing page with MOSAIC animated logo and 4 modality icons (L/S/R/W)",
        "Log in as a student (sofia.reyes@ps124.nyc) — show they land directly on their assessment",
        "Start a Listening question with audio playback, then show a Reading passage + multiple choice",
        "For K-2: show the simplified UI with large buttons, bigger text, and dot progress indicators",
        "Show Pause/Resume — click 'Save & Exit' and show the exam continues exactly where they left off",
      ]} />
      <TalkBlock text={`"Let me start by showing you what your students will actually see. When Sofia logs in, she doesn't see a menu or grade choices — she goes straight to her assessment. This is intentional. We designed MOSAIC so that younger students especially aren't overwhelmed. For K-2 students, the entire interface adapts — larger buttons, simpler navigation, dot progress instead of numbers. And if the bell rings mid-assessment? Students hit 'Save & Exit' and pick up exactly where they left off next period. Zero lost progress."`} />
      <HookBlock hooks={[
        { tag: "ELL Director", tagClass: TAG.ell, text: "\"This gives students practice across the four language modalities — same flow and rigor as a standards-based proficiency assessment.\"" },
        { tag: "Principal", tagClass: TAG.principal, text: "\"Zero training needed for students. They log in and start. No onboarding costs.\"" },
        { tag: "Coach", tagClass: TAG.coach, text: "\"Notice the K-2 adaptation — age-appropriate UX, not just font scaling.\"" },
        { tag: "Parent", tagClass: TAG.parent, text: "\"Your child's practice experience looks and feels like the real test they'll take in spring.\"" },
      ]} />
    </Section>

    <Section num="2" title="The Teacher Dashboard — Where Instruction Meets Data" time="5 min">
      <ShowBlock items={[
        "Log in as Teacher (demo.teacher@ps124.nyc) — show overview with 14 classes, 45 students, pending grading count",
        "Assignments tab — assign an assessment to a class with a due date and modality selection",
        "Grading tab — demonstrate the rubric-aligned scoring interface for Speaking/Writing",
        "Click any student name for the Student Snapshot Popover (composite level, growth trend, modality rings)",
        "Recent Results — show WIDA Level Rings and Can-Do badges on scored assessments",
        "Classes tab — show class creation, CSV import for bulk student enrollment",
      ]} />
      <TalkBlock text={`"Here's where your teachers live. They assign assessments with one click — pick the class, set a due date, choose modalities. Listening and Reading are auto-scored instantly. For Speaking and Writing, teachers use this grading interface — the student's response on one side, proficiency rubrics on the other. One click: scored. And watch this — I hover over any student and get their complete proficiency snapshot. Composite level, growth trend, strongest and weakest modality. No spreadsheets, no digging."`} />
      <HookBlock hooks={[
        { tag: "ELL Director", tagClass: TAG.ell, text: "\"Consistent assessments and data across every classroom in the district.\"" },
        { tag: "Principal", tagClass: TAG.principal, text: "\"Teachers get grading done faster. Better data for parent conferences and IEP reviews.\"" },
        { tag: "AP", tagClass: TAG.ap, text: "\"Check completion rates from the Assignments tab — no chasing down teachers.\"" },
        { tag: "Coach", tagClass: TAG.coach, text: "\"Student Snapshots are gold for coaching conversations — modality-level insight at a glance.\"" },
      ]} />
    </Section>

    <Section num="3" title="Adaptive Assessment & Per-Modality Growth Tracking" time="4 min">
      <ShowBlock items={[
        "ExamSetup screen — show the proficiency panel with color-coded per-modality proficiency levels",
        "Toggle ON Adaptive Mode — explain how difficulty adjusts per modality independently",
        "During exam: show the diamond badge in header and 'L{level}' badges on modality tabs",
        "Growth tab — select a class and show the multi-line trend chart (4 modality lines)",
        "Expand a student row — show per-modality sparkline charts with growth deltas",
        "Class-level modality performance cards (avg score, growth, improvement counts per modality)",
      ]} />
      <TalkBlock text={`"This is where MOSAIC separates itself from every other practice tool on the market. When a student starts an exam, the platform already knows their proficiency in each modality — Listening might be at Level 3, but Writing might be at Level 1. Adaptive mode tailors question difficulty independently per modality. A student who's strong in Reading won't get bored with easy questions, while their Writing practice stays at the right challenge level. And look at this Growth tab — you can track each modality over time with individual trend lines. Expand any student and you see sparkline charts showing their trajectory in each domain."`} />
      <HookBlock hooks={[
        { tag: "ELL Director", tagClass: TAG.ell, text: "\"Per-modality adaptation means every student gets exactly the right difficulty. District-wide.\"" },
        { tag: "Principal", tagClass: TAG.principal, text: "\"This is differentiated instruction at scale — no extra prep work for teachers.\"" },
        { tag: "Coach", tagClass: TAG.coach, text: "\"Modality sparklines show you exactly where each student needs targeted support.\"" },
      ]} />
    </Section>

    <Section num="4" title="Goals, Projections & Intervention Planning" time="4 min">
      <ShowBlock items={[
        "Growth tab — click 'Set Goals' on a student to open the Goal Setting Modal",
        "Set per-modality target proficiency levels (e.g., Listening: 3.5, Writing: 2.0) with target date and IEP notes",
        "Show progress bars in Growth Tracker (current vs. goal %) and dashed goal lines on sparklines",
        "Click 'Export PDF Report' — show the printable growth report with modality table and Can-Do descriptors",
        "Projections tab — show Trajectory Summary (6 status cards: On Track, At Risk, Needs Intervention, Growing, Stable, Declining)",
        "Expand a student to show the projection gauges (current/projected/goal) per modality",
      ]} />
      <TalkBlock text={`"Teachers can set specific WIDA targets for each student — per modality. Maybe Sofia's IEP says she needs to hit Level 2.5 in Writing by June. Set it right here, add the IEP conference notes. Now every time you look at the Growth Tracker, you see progress bars showing how close she is. And here's the game-changer — the Projections tab. MOSAIC uses each student's growth trajectory to project where they'll be by end of year. It sorts students by urgency: 'Needs Intervention' first, then 'At Risk.' If a student is trending below their goal, you see it now — in March — not in June when it's too late."`} />
      <HookBlock hooks={[
        { tag: "ELL Director", tagClass: TAG.ell, text: "\"Predictive projections give you months of lead time to reallocate support resources.\"" },
        { tag: "Principal", tagClass: TAG.principal, text: "\"This is the report you show at your next SLT meeting. Proactive, not reactive.\"" },
        { tag: "AP", tagClass: TAG.ap, text: "\"Goal setting ties directly to IEP conversations — everything in one place.\"" },
        { tag: "Coach", tagClass: TAG.coach, text: "\"When you see 'At Risk' in Writing, you know exactly which modality PD to plan.\"" },
      ]} />
    </Section>

    <Section num="5" title="Cohort Comparison & Analytics — The Strategic View" time="4 min">
      <ShowBlock items={[
        "Cohort tab — toggle between By Grade / By School / By Class views",
        "Switch visualization: Bar Chart, Heatmap, and Detailed Table",
        "Show Systemic Gap alerts (red badges when a modality is below average or growth is flat)",
        "Cross-cohort summary cards (best-performing cohort, cohort needing PD)",
        "Analytics tab — show overview stats, WIDA distribution histogram, grade breakdown",
        "Show PDF report export and CSV data export",
      ]} />
      <TalkBlock text={`"This is where MOSAIC becomes your strategic planning tool. The Cohort Comparison dashboard lets you compare performance across grades, schools, or classrooms side by side. Switch to heatmap view and patterns jump out — maybe Grade 3-4 is strong in Listening but weak in Writing across every school. That's a systemic gap, and MOSAIC flags it automatically. Every piece of this data exports to PDF for board presentations or CSV for your own analysis tools."`} />
      <HookBlock hooks={[
        { tag: "ELL Director", tagClass: TAG.ell, text: "\"This is your board presentation, ready-made. School rankings, systemic gaps, modality trends.\"" },
        { tag: "Principal", tagClass: TAG.principal, text: "\"Heatmap view — one glance tells you where every grade stands.\"" },
        { tag: "Coach", tagClass: TAG.coach, text: "\"Systemic gap alerts tell you exactly where to focus building-level PD this quarter.\"" },
      ]} />
    </Section>

    <Section num="6" title="Admin Dashboard & Platform Operations" time="4 min">
      <ShowBlock items={[
        "Super Admin Dashboard — platform stats, user management, school licensing",
        "Operations tab — show Maintenance Mode toggle, Feature Flags, Data Integrity Health Check",
        "Audit Log — show timestamped record of every admin action",
        "Purchase Orders — show PO creation, license tier management, renewal tracking",
        "Changelog tab — show the visual timeline of all platform releases",
      ]} />
      <TalkBlock text={`"For IT and central office, MOSAIC is enterprise-ready. The Super Admin dashboard gives you full control — Maintenance Mode for testing windows, Feature Flags to roll features out gradually, and a Data Integrity Health Check that automatically scans for orphaned sessions or broken data. Every action is logged in the audit trail."`} />
    </Section>

    <Section num="7" title="Trust, Security & ERMA/FERPA Compliance" time="3 min">
      <ShowBlock items={[
        "ERMA/Compliance tab — Data Retention policies, COPPA Consent management, Breach Incident logging",
        "Privacy Policy (/privacy), Terms of Service (/terms), Subprocessor List (/subprocessors)",
        "Parent Data Export — FERPA-compliant JSON export of all child data",
        "Student Data Deletion — complete removal workflow for Ed Law 2-d compliance",
        "Anti-cheating: tab-switching detection, violation flagging, session protection",
      ]} />
      <TalkBlock text={`"This is the section that gets past procurement. MOSAIC is built for ERMA review from day one. We have a complete Data Privacy Agreement template ready for NYCPS signing. FERPA, COPPA, Ed Law 2-d — every compliance requirement is addressed with actual features, not just policies. Parents can request a full data export of their child's records. Administrators can delete student data completely."`} />
      <HookBlock hooks={[
        { tag: "ELL Director", tagClass: TAG.ell, text: "\"ERMA submission documents are pre-built — DPA, Incident Response Plan, Cover Letter.\"" },
        { tag: "Principal", tagClass: TAG.principal, text: "\"When a parent asks 'what data do you have on my child?' — one click to export everything.\"" },
        { tag: "Sales", tagClass: TAG.sales, text: "\"This is your objection killer when procurement says 'we need to review data privacy.'\"" },
      ]} />
    </Section>

    <Section num="8" title="WIDA Standards Deep Integration — Our Differentiator" time="4 min">
      <ShowBlock items={[
        "WIDA Standards tab — show the full interactive rubric training module for Writing and Speaking",
        "Practice scoring a sample response — show the score-and-feedback loop with explanation",
        "Navigate to any scored exam result — point out ELD Standard badges (1-5) and Key Language Use tags (Narrate/Inform/Explain/Argue) on every question",
        "Expand a modality section — show the proficiency descriptors panel with 'what they CAN do' and 'growth target for next level'",
        "Show Results Detail page — four Can-Do panels (L/S/R/W) at the student's current level with green dots for current abilities and blue dots for next-level targets",
      ]} />
      <TalkBlock text={`"Here's what makes MOSAIC fundamentally different from every other practice tool. We don't just test the four modalities — every single question is mapped to language development standards and key language uses. So a teacher doesn't just know a student scored a 3 in Reading — they know the student scored a 3 on Reading for Social Studies using the 'Inform' language function. And here's the proficiency descriptors panel — at any student's result, we show exactly what that student CAN do right now, plus exactly what the next level looks like as a growth target. And the Mosaic Proficiency Rubric and Calibration module — teachers can practice scoring Writing and Speaking responses with real samples and get immediate feedback on whether their scoring is calibrated. We include it free."`} />
      <HookBlock hooks={[
        { tag: "ELL Director", tagClass: TAG.ell, text: "\"Every question is designed around language-development skills and key language uses — this isn't a generic test bank; it's built to help educators prepare for New York's 2027 assessment transition.\"" },
        { tag: "Principal", tagClass: TAG.principal, text: "\"Built-in rubric training means consistent scoring across all your teachers — no separate PD sessions needed.\"" },
        { tag: "Coach", tagClass: TAG.coach, text: "\"Proficiency descriptors at each student's level — this is the reference tool teachers need during IEP meetings and parent conferences.\"" },
        { tag: "AP", tagClass: TAG.ap, text: "\"Key Language Use tags let you analyze whether students struggle more with Narrate vs. Explain tasks — that's instructional planning gold.\"" },
      ]} />
    </Section>

    <Section num="9" title="The Close — Next Steps" time="3 min">
      <TalkBlock text={`"That's MOSAIC. A purpose-built English language proficiency assessment platform. Every question is mapped to five ELD Standards, four Key Language Uses, and six proficiency levels. Adaptive difficulty adjusts per modality. Built-in Can-Do Descriptors show what every student can do right now. Growth tracking, predictive projections, and cohort analysis give you data you can actually act on. Your students practice on a platform purpose-built for English language proficiency. Your teachers get actionable insights and free rubric calibration training. And you get the visibility to drive results across your building — or your entire district. The platform is ready to go. Let's talk about what a pilot would look like for your school."`} />
      <div className="bg-[#002FA7] text-white p-4 rounded">
        <div className="font-bold text-sm mb-2">Closing Questions (Pick 2-3)</div>
        <ol className="text-xs space-y-1 opacity-90 list-decimal list-inside">
          <li>"Which grades would you want to pilot first?"</li>
          <li>"How many ELL students are in your building/district right now?"</li>
          <li>"When is your next ACCESS testing window?"</li>
          <li>"Who else on your team would need to see this before a decision?"</li>
          <li>"Would a 30-day pilot with real student accounts help you evaluate this?"</li>
        </ol>
      </div>
      <TipBlock title="POST-DEMO ACTION" items={[
        "Send the audience-specific one-pager within 2 hours (not 24 — urgency matters)",
        "Offer: \"I can set up a pilot with real student accounts in 48 hours\"",
        "Schedule the next call within the demo itself — don't leave it open-ended",
      ]} />
    </Section>
  </div>
);

// ============ PART II: AUDIENCE GUIDES ============
const AudienceGuides = () => (
  <div className="space-y-2">
    <div className="bg-[#002FA7] text-white p-4 mb-4">
      <div className="text-[10px] font-bold tracking-widest opacity-70">PART II</div>
      <div className="text-lg font-extrabold">Audience-Specific Presentation Guides</div>
      <p className="text-xs opacity-80">Tailored demo flows for each buyer persona. Use when you know your audience.</p>
    </div>

    <Section num="A" title="For District ELL Directors / Coordinators" time="20 min">
      <TipBlock title="WHAT THEY CARE ABOUT" items={[
        "District-wide visibility into ELL performance by school, grade, and modality",
        "Standards alignment — how does this map to ELD Standards and Key Language Uses?",
        "Data for board presentations, SLT meetings, and state reporting",
        "Equity — are gaps between schools/grades being identified and addressed?",
        "ERMA compliance — will this pass procurement review?",
      ]} />
      <ShowBlock items={[
        "1. (2 min) Student experience — four-modality flow and K-2 adaptation",
        "2. (3 min) Teacher Dashboard — assignments, grading, student snapshots",
        "3. (4 min) Cohort Comparison — By School view with heatmap showing cross-school gaps",
        "4. (3 min) Projections — 'Needs Intervention' students and trajectory projections",
        "5. (3 min) Analytics — WIDA distribution, grade breakdown, PDF/CSV export",
        "6. (3 min) ERMA Compliance — DPA, data retention, parent export, breach logging",
        "7. (2 min) Close — 'Which schools would you want to start with?'",
      ]} />
      <TalkBlock text={`"MOSAIC gives you the district-level visibility that doesn't exist today. Instead of waiting for ACCESS results in the summer, you'll have real-time data on every ELL student in every school — with the ability to identify systemic gaps and reallocate resources before the testing window."`} />
    </Section>

    <Section num="B" title="For Principals & Assistant Principals" time="15 min">
      <TipBlock title="WHAT THEY CARE ABOUT" items={[
        "How does this reduce teacher workload, not add to it?",
        "Data for observations, IEP meetings, and parent conferences",
        "Implementation ease — will this work on our existing devices?",
        "Cost — what does this cost per student and does it fit our budget?",
        "Accountability — can I see which teachers are using it and how often?",
      ]} />
      <ShowBlock items={[
        "1. (2 min) Student experience — 'Zero training, any browser, any device'",
        "2. (4 min) Teacher Dashboard — auto-scoring for L/R, grading interface for S/W, snapshots",
        "3. (3 min) Growth Tracking — class-level modality cards, student sparklines",
        "4. (3 min) Projections — 'Here's where each student is headed by June'",
        "5. (1 min) PDF Reports — exportable growth report for IEP meetings",
        "6. (2 min) Close — 'Would a pilot in one grade band help you evaluate this?'",
      ]} />
      <TalkBlock text={`"MOSAIC doesn't create more work for your teachers — it eliminates busywork. Auto-scored Listening and Reading. One-click grading for Speaking and Writing. Growth reports that export to PDF for IEP meetings. And projections that tell you which students need intervention now, not after ACCESS results come back in July."`} />
    </Section>

    <Section num="C" title="For Teachers & Instructional Coaches" time="15 min">
      <TipBlock title="WHAT THEY CARE ABOUT" items={[
        "Is this easy to use? Will it take me 5 minutes or 50 to get started?",
        "Does this give me data I can actually use in my instruction?",
        "How are the questions aligned to WIDA? Can I trust the scores?",
        "Can I set it up for my class and forget about it, or do I need to manage things daily?",
        "Can I customize or add my own questions?",
      ]} />
      <ShowBlock items={[
        "1. (1 min) Create a class and add students (or CSV import)",
        "2. (2 min) Assign an assessment — pick modalities, set due date",
        "3. (2 min) Student takes assessment — show the flow they'll see",
        "4. (3 min) Grade Speaking/Writing responses with the rubric interface",
        "5. (2 min) Growth tab — class modality cards and student sparklines",
        "6. (2 min) Set a goal for a student, show progress bars and projection gauges",
        "7. (1 min) Export PDF growth report — 'This is your IEP meeting, done'",
        "8. (2 min) My Questions tab — show custom question creation (if relevant)",
      ]} />
      <TalkBlock text={`"MOSAIC is the tool I wish I had when I was teaching ENL. Set up once — create your class, import your roster. Assign assessments and the auto-scoring handles Listening and Reading. For Speaking and Writing, you'll spend minutes, not hours, scoring with the rubric interface. Then you get the data that matters — which modality does each student need the most help in? Set goals, track progress, export reports. It's your entire assessment workflow in one place."`} />
    </Section>

    <Section num="D" title="For Parents & Community Presentations" time="10 min">
      <TipBlock title="WHAT THEY CARE ABOUT" items={[
        "Is my child's data safe? What do you collect?",
        "What will my child experience? Is it stressful?",
        "How does this help my child succeed on the real ACCESS test?",
        "Can I see my child's progress?",
      ]} />
      <ShowBlock items={[
        "1. (2 min) Student experience — how friendly and low-stress the interface is",
        "2. (2 min) K-2 adaptation — 'Designed for young learners, not miniature adults'",
        "3. (3 min) Growth report — sample PDF with modality scores and Can-Do descriptors",
        "4. (2 min) Privacy — explain FERPA, COPPA, parent data export rights",
        "5. (1 min) Q&A",
      ]} />
      <TalkBlock text={`"MOSAIC helps your child practice for their English proficiency test in a way that feels comfortable and familiar. The practice looks exactly like the real test, so there are no surprises on test day. Your child's teacher sets specific goals and tracks progress. You can request a full export of your child's data at any time — it's your right under federal law, and MOSAIC makes it easy. Your child's data is never sold or shared outside the school."`} />
    </Section>
  </div>
);

// ============ PART III: SALES TRAINING ============
const SalesTraining = () => (
  <div className="space-y-2">
    <div className="bg-[#002FA7] text-white p-4 mb-4">
      <div className="text-[10px] font-bold tracking-widest opacity-70">PART III</div>
      <div className="text-lg font-extrabold">Sales Team Training & Call Playbook</div>
      <p className="text-xs opacity-80">Everything a sales rep needs to qualify, present, handle objections, and close.</p>
    </div>

    <Section num="I" title="Feature Mastery Cheat Sheet">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-[#002FA7] text-white">
              <th className="p-2 text-left font-bold">Category</th>
              <th className="p-2 text-left font-bold">Feature</th>
              <th className="p-2 text-left font-bold">One-Line Value Prop</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Assessment", "4-Modality Exams (L/S/R/W)", "Standards-based English language proficiency assessment format"],
              ["Assessment", "Adaptive Difficulty per Modality", "Every student gets the right challenge level automatically"],
              ["Assessment", "7 Grade Bands (K through 11-12)", "Age-appropriate content from kindergarten to high school"],
              ["Assessment", "K-2 Simplified UI", "Young learners get larger buttons, simpler navigation"],
              ["Assessment", "Pause/Resume with Crash Recovery", "Zero lost progress — even if the browser crashes"],
              ["Standards tagging", "ELD Standard Tags (1-5)", "Every question mapped to Social, Language Arts, Math, Science, or Social Studies"],
              ["Standards tagging", "Key Language Use Labels", "Questions tagged as Narrate, Inform, Explain, or Argue"],
              ["Standards tagging", "Embedded proficiency descriptors", "Shows what students CAN do at their level + next level growth targets"],
              ["Standards tagging", "Rubric Training Module", "Interactive practice scoring with Writing & Speaking samples and feedback"],
              ["Teacher", "Auto-Scoring (Listening/Reading)", "Instant results — teachers only grade Speaking/Writing"],
              ["Teacher", "Per-Modality Goal Setting", "Set IEP-aligned targets per student per modality"],
              ["Teacher", "Predictive Projections", "See where each student will be by June — before it's too late"],
              ["Teacher", "PDF Growth Reports", "One-click printable reports for IEP meetings"],
              ["Analytics", "Per-Modality Growth Tracking", "4 trend lines showing trajectory for each language domain"],
              ["Analytics", "Cohort Comparison", "Compare grades, schools, or classes to find systemic gaps"],
              ["Analytics", "Systemic Gap Detection", "Automated alerts when a modality is below average across cohorts"],
              ["Admin", "5-Tier RBAC", "Student, Teacher, School Admin, District Admin, Super Admin"],
              ["Admin", "Feature Flags & Maintenance Mode", "Enterprise ops — roll out features gradually"],
              ["Compliance", "ERMA/FERPA/COPPA/Ed Law 2-d", "Full compliance suite with DPA template ready for NYCPS"],
              ["Compliance", "Anti-Cheating Engine", "Tab-switch detection, violation logging, session protection"],
            ].map(([cat, feat, val], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="p-2 border-b border-gray-100 font-semibold text-gray-500">{cat}</td>
                <td className="p-2 border-b border-gray-100 font-medium">{feat}</td>
                <td className="p-2 border-b border-gray-100 text-gray-600">{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>

    <Section num="II" title="Buyer Persona Pain Points">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-[#002FA7] text-white">
              <th className="p-2 text-left font-bold">Persona</th>
              <th className="p-2 text-left font-bold">Biggest Pain</th>
              <th className="p-2 text-left font-bold">Doing Today</th>
              <th className="p-2 text-left font-bold">MOSAIC Solve</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["District ELL Director", "No visibility into cross-school ELL performance until ACCESS results", "Waiting for annual results, manual spreadsheets", "Real-time cohort comparison, systemic gap detection, projections"],
              ["Principal", "Teachers overwhelmed; IEP/parent meeting data is scattered", "Multiple tools, printouts from different systems", "One platform: assign, score, track, report. PDF export"],
              ["Assistant Principal", "Hard to monitor teacher implementation and completion", "Asking teachers individually, checking clipboards", "Assignment tracking, completion dashboards, audit trail"],
              ["ENL Teacher", "Scoring Writing/Speaking is time-consuming; no good ACCESS practice tool", "Paper-based practice, generic test prep, manual gradebooks", "Auto-scored L/R, streamlined S/W grading, growth tracking"],
              ["Instructional Coach", "Can't identify modality-specific gaps at building level", "Reviewing individual student data, no cross-class visibility", "Cohort comparison, modality trends, systemic gap alerts"],
              ["Parent", "Doesn't understand ACCESS or how their child is doing", "Waiting for report cards, confused by WIDA levels", "Growth reports with Can-Do descriptors in plain language"],
            ].map(([persona, pain, today, solve], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="p-2 border-b border-gray-100 font-bold">{persona}</td>
                <td className="p-2 border-b border-gray-100 text-gray-600">{pain}</td>
                <td className="p-2 border-b border-gray-100 text-gray-500">{today}</td>
                <td className="p-2 border-b border-gray-100 text-green-700 font-medium">{solve}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>

    <Section num="III" title="The Sales Call Framework">
      <TipBlock title="CALL STRUCTURE (30 MIN)" items={[
        'Minutes 0-5: DISCOVERY. Ask questions. Understand role, school/district size, current tools, biggest frustration. DO NOT demo yet.',
        'Minutes 5-7: POSITION. "Based on what you\'ve shared, let me show you three things I think will matter most." Pick 3 features.',
        'Minutes 7-22: TARGETED DEMO. Show ONLY what\'s relevant. A principal doesn\'t need ERMA compliance. Use the audience flows in Part II.',
        'Minutes 22-25: SOCIAL PROOF. "441 language-proficiency practice questions," "Full ERMA compliance suite," "Adaptive difficulty per modality — no other tool does this."',
        'Minutes 25-30: CLOSE. Ask for the next step. Always leave with a scheduled follow-up — never "I\'ll get back to you."',
      ]} />
      <TipBlock title="6 GOLDEN RULES" items={[
        'Rule 1: Never demo without discovery. 5 minutes of questions saves 20 minutes of irrelevant features.',
        'Rule 2: Show, don\'t tell. Don\'t say "we have analytics." Log in and show the heatmap.',
        'Rule 3: Use their language. Say "ACCESS" not "assessment." Say "SLT" not "analytics dashboard."',
        'Rule 4: Anchor to their timeline. "When is your next ACCESS window?" Then connect to pilot timing.',
        'Rule 5: Always close with a next step. "Can we schedule 15 minutes next Tuesday?" Never end with "let me know."',
        'Rule 6: Follow up within 2 hours. Send the one-pager and a personalized note — not a template.',
      ]} />
    </Section>

    <Section num="IV" title="Objection Handling Playbook">
      <ObjectionBlock
        question={'"We already have a test prep tool."'}
        answer={'"That\'s great — which one? [Listen.] Most test prep tools are generic multiple-choice platforms with WIDA content added. MOSAIC is purpose-built for New York\'s 2027 English Language Proficiency Assessment transition. We don\'t just have questions — we have adaptive difficulty per modality, per-domain proficiency scoring on the 1.0-6.0 scale, and predictive projections that tell you where each student will be by June. Let me show you the difference."'}
      />
      <ObjectionBlock
        question={'"We don\'t have budget for this right now."'}
        answer={'"Completely understand. Two things: First, MOSAIC has flexible licensing tiers — K-5, K-8, 6-8, 9-12, or All Grades — so you only pay for what you need. Second, most districts fund this through Title III ELL funds, which are specifically designated for English learner programs. Would it help if I put together a funding justification document?"'}
      />
      <ObjectionBlock
        question={'"Our teachers are already overwhelmed."'}
        answer={'"That\'s exactly why we built it this way. Teachers create a class, import students via CSV, assign an assessment — done. Listening and Reading are auto-scored. Speaking and Writing grading takes about 2 minutes per student. The system actually saves teachers time compared to paper-based practice."'}
      />
      <ObjectionBlock
        question={'"How do we know this will pass ERMA / data privacy review?"'}
        answer={'"We built MOSAIC to pass ERMA from day one. We have a pre-built Data Privacy Agreement template ready for NYCPS signing. FERPA, COPPA, Ed Law 2-d — every requirement is addressed with actual features. I can send you the full ERMA submission package — DPA, Incident Response Plan, and Cover Letter — today."'}
      />
      <ObjectionBlock
        question={'"We need SSO / student information system integration."'}
        answer={'"We support Azure AD for NYCPS districts and Clever for broader K-12 integration. For the pilot, email/password login takes 30 seconds to set up. Once you move forward, we configure SSO with your IT team — most setup is under a day."'}
      />
      <ObjectionBlock
        question={'"Can students cheat on this?"'}
        answer={'"We have a built-in anti-cheating engine. Tab-switch detection, copy-paste blocking, and flagging suspicious sessions for teacher review. Plus session protection with heartbeat monitoring — if a browser crashes, progress is saved. Teachers see every flagged session in the Exam Integrity tab."'}
      />
      <ObjectionBlock
        question={'"How aligned are you with the 2027 New York assessment?"'}
        answer={'"We\'re aligned at the framework level — not just the test format. Every single question is tagged to one of the 5 language development standards and 4 key language uses. Results include embedded proficiency descriptors showing exactly what each student can do at their current level, plus the next level as a growth target. We even include a free Mosaic Proficiency Rubric and Calibration module so your teachers can practice their Writing and Speaking scoring. No other practice tool does this."'}
      />
      <ObjectionBlock
        question={'"What devices does this run on?"'}
        answer={'"Any device with a browser. Chromebooks, iPads, laptops, desktops — no software to install. If your students can open Chrome, they can use MOSAIC. For Speaking tasks, they need a microphone, which is built into most Chromebooks and tablets."'}
      />
    </Section>

    <Section num="V" title="Discovery Questions & Qualification">
      <TipBlock title="MUST-ASK QUESTIONS (FIRST 5 MINUTES)" items={[
        '"What\'s your role, and how are you involved with ELL programs?"',
        '"How many ELL students are in your building/district?"',
        '"What are you using today to prepare students for WIDA ACCESS?"',
        '"What\'s the biggest challenge you face with ACCESS prep right now?"',
        '"When is your next ACCESS testing window?"',
      ]} />
      <TipBlock title="DEEPER DIVE QUESTIONS" items={[
        '"How do your teachers currently track student growth across modalities?"',
        '"Do you have visibility into how different grades or schools are performing relative to each other?"',
        '"How do you handle IEP data and parent conference prep for ELL students?"',
        '"Has ERMA review been a blocker for adopting edtech tools in the past?"',
        '"What would a successful pilot look like for you?"',
        '"Who else would need to be involved in the decision — IT, procurement, other administrators?"',
        '"Is this funded through Title III, or a different budget line?"',
      ]} />
      <div className="bg-slate-50 border border-slate-200 p-4 rounded">
        <div className="text-[10px] font-extrabold text-slate-700 tracking-wider mb-2">QUALIFICATION SIGNALS</div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <div className="font-bold text-green-700 mb-1">Hot Lead</div>
            <p className="text-gray-600">Mentions ACCESS by name, has 50+ ELL students, testing window within 3 months</p>
          </div>
          <div>
            <div className="font-bold text-amber-600 mb-1">Warm Lead</div>
            <p className="text-gray-600">Exploring options, district pushing for data, mentions "NYSESLAT transition"</p>
          </div>
          <div>
            <div className="font-bold text-gray-500 mb-1">Cold Lead</div>
            <p className="text-gray-600">"Just looking," no timeline, no clear pain, no decision-making authority</p>
          </div>
        </div>
      </div>
    </Section>

    <Section num="VI" title="Competitive Positioning">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-[#002FA7] text-white">
              <th className="p-2 text-left font-bold">Capability</th>
              <th className="p-2 text-center font-bold">MOSAIC</th>
              <th className="p-2 text-center font-bold">Generic Test Prep</th>
              <th className="p-2 text-center font-bold">Paper-Based</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Proficiency scoring (1.0-6.0 scale)", "Yes", "Varies (many use %)", "Manual"],
              ["4 modalities (L/S/R/W)", "All 4 with audio/STT", "Usually L/R only", "All 4 (no audio)"],
              ["ELD Standard tagging (1-5)", "All questions tagged", "No", "No"],
              ["Key Language Use labels", "Narrate/Inform/Explain/Argue", "No", "No"],
              ["Proficiency descriptors embedded", "Per-modality, per-level", "No", "No"],
              ["Rubric training module", "Built-in (free)", "No", "External PD ($)"],
              ["Adaptive per modality", "Yes", "No", "No"],
              ["K-2 UI adaptation", "Yes", "Rarely", "N/A"],
              ["Per-modality growth tracking", "Yes", "No", "No"],
              ["Predictive projections", "Yes", "No", "No"],
              ["Cohort comparison", "Yes", "Limited", "No"],
              ["Goal setting with IEP notes", "Yes", "No", "Manual"],
              ["PDF growth reports", "One-click", "No", "Manual creation"],
              ["ERMA/FERPA compliance suite", "Built-in", "Varies", "N/A"],
              ["Anti-cheating", "Yes", "Basic", "N/A"],
              ["Pause/resume + crash recovery", "Yes", "Sometimes", "N/A"],
            ].map(([cap, mosaic, generic, paper], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="p-2 border-b border-gray-100 font-medium">{cap}</td>
                <td className="p-2 border-b border-gray-100 text-center font-bold text-green-700">{mosaic}</td>
                <td className="p-2 border-b border-gray-100 text-center text-gray-500">{generic}</td>
                <td className="p-2 border-b border-gray-100 text-center text-gray-400">{paper}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TalkBlock text={`"Most test prep platforms were built for generic standardized tests and then had WIDA content bolted on. MOSAIC was built from the ground up for WIDA — every question, every scoring formula, every report speaks the language of WIDA. That's why we're the only platform with adaptive difficulty per modality and predictive end-of-year projections."`} />
    </Section>

    <Section num="VII" title="Post-Call Checklist & Follow-Up">
      <TipBlock title="WITHIN 2 HOURS OF THE CALL" items={[
        "Send personalized follow-up email (reference something specific they said)",
        "Attach the audience-specific one-pager",
        "If they mentioned ERMA: attach DPA template and ERMA cover letter",
        "If they asked about pricing: send pricing framework with Title III funding angle",
      ]} />
      <TipBlock title="WITHIN 24 HOURS" items={[
        "Log the call in CRM: contact info, school/district, ELL student count, timeline, next step",
        "If pilot was discussed: create school and teacher accounts (can be done in 48 hours)",
        "Set a calendar reminder for follow-up (never more than 1 week out)",
      ]} />
      <TipBlock title="IF NO RESPONSE AFTER 3 DAYS" items={[
        'Send a "nudge" email with one additional data point or feature they didn\'t see',
        "Reference their ACCESS testing window timeline",
        'Offer a different stakeholder demo: "Would it help if I showed this to your ENL coordinator directly?"',
      ]} />
    </Section>
  </div>
);

// ============ MAIN COMPONENT ============
const PresentationPlaybook = () => {
  const [activePart, setActivePart] = useState("demo");

  const handlePrint = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/sales/presentation-playbook.html`, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="presentation-playbook">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
            <Presentation size={20} className="text-[#002FA7]" />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
              Presentation Playbook
            </h2>
            <p className="text-sm text-gray-500">Demo scripts, audience guides, and sales training — all in one place</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-playbook-btn">
          <Printer size={14} className="mr-1" /> Print Version
        </Button>
      </div>

      {/* Part Selector */}
      <div className="flex gap-2 flex-wrap" data-testid="playbook-part-selector">
        {PARTS.map(part => {
          const Icon = part.icon;
          return (
            <Button
              key={part.id}
              variant={activePart === part.id ? "default" : "outline"}
              onClick={() => setActivePart(part.id)}
              className={`btn-sharp ${activePart === part.id ? 'bg-[#002FA7] text-white' : ''}`}
              data-testid={`playbook-part-${part.id}`}
            >
              <Icon size={14} className="mr-1.5" />
              {part.label}
            </Button>
          );
        })}
      </div>

      {/* Content */}
      {activePart === "demo" && <DemoScript />}
      {activePart === "audiences" && <AudienceGuides />}
      {activePart === "sales" && <SalesTraining />}
    </div>
  );
};

export default PresentationPlaybook;
